import * as React from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Link,
  ListItemIcon,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { CardBase } from "../../components/global";
import { NegotiationStatus, Project, ProjectStatus } from "../../types/project";
import { useNavigate } from "react-router-dom";
import { useUILabel } from "../../hooks/useUILabel";
import MapPreview from "../../components/MapPreview";
import {
  AssignmentOutlined,
  ContactPhone,
  ContentCopy,
  DeleteForeverOutlined,
  Edit,
  EditOutlined,
  EmailOutlined,
  ExpandMore,
  FilePresent,
  RemoveCircleOutlineOutlined,
  UploadFile,
  Settings,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { nl2br } from "../../utils/helper";
// import EditIcon from "@mui/icons-material/Edit";
import ModalStartDateForm from "./ModalStartDateForm";
import {
  useDeleteProjectDocumentMutation,
  useUploadProjectDocumentMutation,
} from "../../services/project.service";
import { useFilePicker } from "use-file-picker";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";
import ModalInternalNotesForm from "./ModalInternalNotesForm";

import { useCompany } from "../../hooks/useCompany";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { CompanyStatus } from "../../types/company";
import { formatWithTimezone } from "../../utils/dateHelper";
import { decode } from "html-entities";
import CustomizeLayoutModal, {
  CARD_IDS,
  DEFAULT_CARD_ORDER,
} from "../../components/CustomizeLayoutModal";
import {
  useGetCardPreferencesQuery,
  useUpdateCardPreferencesMutation,
  CARD_PREFERENCE_TYPE,
  CARD_PREFERENCE_PAGE,
} from "../../services/cardPreferences.service";

const ProjectDetailsPanel = ({ data }: { data?: Project; }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // UI Labels
  const scopeOfWorkLabel = useUILabel("project.scopeOfWork");
  const confidentialInformationLabel = useUILabel("project.confidentialInformation");
  const workDocumentsLabel = useUILabel("project.workDocuments");
  const noDocumentsSpecifiedLabel = useUILabel("project.noDocumentsSpecified");
  const viewDocumentLabel = useUILabel("project.viewDocument");
  const uploadDocumentLabel = useUILabel("project.uploadDocument");
  const startTimeLabel = useUILabel("project.startTime");
  const reasonScheduleLabel = useUILabel("project.reasonSchedule");
  const estimatedAmountOfHoursLabel = useUILabel("project.estimatedAmountOfHours");
  const tasksLabel = useUILabel("project.tasks");
  const noTasksSpecifiedLabel = useUILabel("project.noTasksSpecified");
  const requirementsLabel = useUILabel("project.requirements");
  const skillsLabel = useUILabel("project.skills");
  const certificationsLabel = useUILabel("project.certifications");
  const notRequiredLabel = useUILabel("project.notRequired");
  const additionalSettingsLabel = useUILabel("project.additionalSettings");
  const photosRequiredLabel = useUILabel("project.photosRequired");
  const finalNotesRequiredLabel = useUILabel("project.finalNotesRequired");
  const toolsRequiredLabel = useUILabel("project.toolsRequired");
  const mileRadiusLabel = useUILabel("project.mileRadius");
  const backgroundCheckRequiredLabel = useUILabel("project.backgroundCheckRequired");
  const drugTestRequiredLabel = useUILabel("project.drugTestRequired");
  const workSiteLabel = useUILabel("project.workSite");
  const pricingLabel = useUILabel("project.pricing");
  const fixedRateLabel = useUILabel("project.fixedRateLabel");
  const hourlyRateLabel = useUILabel("project.hourlyRate");
  const managerLabel = useUILabel("manager.manager");
  const internalNotesLabel = useUILabel("project.internalNotes");
  const metadataLabel = useUILabel("project.metadata");
  const createdAtLabel = useUILabel("project.createdAt");
  const lastUpdateLabel = useUILabel("project.lastUpdate");
  const yesLabel = useUILabel("project.yes");
  const noLabel = useUILabel("project.no");
  const customizeLayoutButtonLabel = useUILabel("project.customizeLayout");
  const createTemplateFromThisWorkLabel = useUILabel("project.createTemplateFromThisWork");

  const [uploadDoc, { isLoading: uploading }] =
    useUploadProjectDocumentMutation();
  const [deleteDoc] = useDeleteProjectDocumentMutation();
  const company = useCompany();
  const user = useCurrentUser();

  const [openFileSelector, { plainFiles, loading, errors, clear }] =
    useFilePicker({
      multiple: false,
      accept: [
        ".doc",
        ".docx",
        ".pdf",
        ".txt",
        ".jpeg",
        ".jpg",
        ".png",
        ".gif",
        ".pptx",
        ".zip",
        ".xls",
        "xlsx",
        ".csv",
      ],
      maxFileSize: 50,
      readFilesContent: false,
    });

  const [formStartDateModal, setFormStartDateModal] = React.useState<{
    open: boolean;
    data: Project | null;
  }>({
    open: false,
    data: null,
  });
  const [formInternalNotesModal, setFormInternalNotesModal] = React.useState<{
    open: boolean;
    data: Project | null;
  }>({
    open: false,
    data: null,
  });
  const [customizeLayoutModalOpen, setCustomizeLayoutModalOpen] =
    React.useState(false);

  // Load card preferences for project page
  const { data: cardPreferences } = useGetCardPreferencesQuery({
    type: CARD_PREFERENCE_TYPE.CUSTOMER,
    page: CARD_PREFERENCE_PAGE.PROJECT,
  });
  const [updateCardPreferences] = useUpdateCardPreferencesMutation();

  // Get ordered and visible cards
  const cardOrder = React.useMemo(() => {
    // If cardOrder is empty array or undefined, use default
    if (!cardPreferences?.cardOrder || cardPreferences.cardOrder.length === 0) {
      return DEFAULT_CARD_ORDER;
    }
    return cardPreferences.cardOrder;
  }, [cardPreferences]);

  const hiddenCards = React.useMemo(() => {
    // If hiddenCards is undefined, use empty array
    return cardPreferences?.hiddenCards || [];
  }, [cardPreferences]);

  const [lng, lat] = React.useMemo(() => {
    if (!!data?.customer) {
      return data?.customer?.coordinate?.coordinates || [];
    } else if (!!data?.location) {
      return data?.location?.coordinate?.coordinates || [];
    } else return [];
  }, [data]);

  const resource = React.useMemo(() => {
    const res = data?.resources?.filter(
      (it) =>
        it.status == NegotiationStatus.HIRED ||
        it.status == NegotiationStatus.COMPLETED
    )?.[0];
    return res;
  }, [data]);

  const _uploadDoc = async (file: File) => {
    try {
      await uploadDoc({ projectId: data?.id!, file }).unwrap();
      clear();
    } catch (err: any) {
      toast.error(err.message);
      clear();
    }
  };

  React.useEffect(() => {
    if (plainFiles?.length > 0) {
      const file = plainFiles[0];
      _uploadDoc(file);
    }
  }, [plainFiles]);

  React.useEffect(() => {
    errors?.[0]?.fileSizeTooSmall && toast.error("File size is too small!");
    errors?.[0]?.fileSizeToolarge && toast.error("File size is too large!");
    errors?.[0]?.readerError &&
      toast.error("Problem occured while reading file!");
    errors?.[0]?.maxLimitExceeded && toast.error("Too many files");
    errors?.[0]?.minLimitNotReached && toast.error("Not enought files");
  }, [errors]);

  const handleSaveCardPreferences = async (preferences: {
    cardOrder: string[];
    hiddenCards: string[];
  }) => {
    try {
      await updateCardPreferences({
        type: CARD_PREFERENCE_TYPE.CUSTOMER,
        page: CARD_PREFERENCE_PAGE.PROJECT,
        data: {
          cardOrder: preferences.cardOrder,
          hiddenCards: preferences.hiddenCards,
          userId: user?.id, // Send user ID for per-user preferences
        },
      }).unwrap();
      toast.success("Layout preferences saved successfully!");
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to save preferences");
      throw error;
    }
  };

  // Helper function to render card by ID
  const renderCard = (cardId: string) => {
    const cardStyle = {
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: (theme: any) =>
          theme.palette.mode === "dark"
            ? "0 8px 24px rgba(0, 0, 0, 0.4)"
            : "0 8px 24px rgba(0, 0, 0, 0.1)",
      },
    };

    switch (cardId) {
      case CARD_IDS.scopeOfWork:
        return (
          <CardBase
            key={cardId}
            title={scopeOfWorkLabel}
            useGlassCard={true}
            nested={true}
            sx={cardStyle}
          >
            <Typography
              variant="body2"
              sx={{ lineHeight: 1.8, fontSize: { xs: "0.95rem", md: "1rem" } }}
            >
              {nl2br(decode(data?.description))}
            </Typography>
          </CardBase>
        );
      case CARD_IDS.confidentialInfo:
        return (
          <CardBase
            key={cardId}
            title={confidentialInformationLabel}
            useGlassCard={true}
            nested={true}
            sx={cardStyle}
          >
            <Typography
              variant="body2"
              sx={{ lineHeight: 1.8, fontSize: { xs: "0.95rem", md: "1rem" } }}
            >
              {nl2br(data?.confidential_information) || "n/a"}
            </Typography>
          </CardBase>
        );
      case CARD_IDS.workDocuments:
        return (
          <CardBase
            key={cardId}
            title={workDocumentsLabel}
            useGlassCard={true}
            nested={true}
            sx={cardStyle}
          >
            {data?.documents?.length == 0 ? (
              <Typography variant="caption" color="text.secondary">
                {noDocumentsSpecifiedLabel}
              </Typography>
            ) : (
              <List sx={{ py: 0 }}>
                {data?.documents?.map((doc) => (
                  <ListItem
                    key={doc.id}
                    sx={{
                      px: { xs: 0, sm: 1 },
                      py: 1.5,
                      borderRadius: "8px",
                      mb: 1,
                      "&:hover": {
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.03)",
                      },
                    }}
                    secondaryAction={
                      <IconButton
                        size="small"
                        onClick={() =>
                          deleteDoc({ projectId: data?.id!, id: doc.id })
                        }
                        sx={{
                          "&:hover": {
                            backgroundColor: "error.light",
                            color: "error.main",
                          },
                        }}
                      >
                        <RemoveCircleOutlineOutlined fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <FilePresent color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${doc.name || "n/a"}`}
                      primaryTypographyProps={{
                        fontWeight: 500,
                        fontSize: 15,
                      }}
                      secondary={
                        <Link
                          href={doc.original_url}
                          target="_blank"
                          sx={{
                            textDecoration: "none",
                            color: "primary.main",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {viewDocumentLabel}
                        </Link>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
            <Box sx={{ mt: 2 }}>
              <LoadingButton
                variant="outlined"
                size="medium"
                onClick={() => openFileSelector()}
                disabled={company?.status != CompanyStatus.ACTIVE}
                startIcon={<UploadFile />}
                loading={uploading}
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {uploadDocumentLabel}
              </LoadingButton>
            </Box>
          </CardBase>
        );
      case CARD_IDS.startTime:
        return (
          <CardBase
            key={cardId}
            title={startTimeLabel}
            useGlassCard={true}
            nested={true}
            sx={cardStyle}
          >
            <Typography variant="body1" fontWeight={500}>
              {!!resource &&
                (data?.status == ProjectStatus.SCHEDULED ||
                  data?.status == ProjectStatus.IN_PROGRESS ||
                  data?.status == ProjectStatus.COMPLETED)
                ? formatWithTimezone(resource?.start_date, resource?.timezone)
                : formatWithTimezone(data?.start_date, data?.timezone)}
            </Typography>
            {!!data?.reason_schedule && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", mt: 1, display: "block" }}
              >
                {reasonScheduleLabel}: {data?.reason_schedule || "n/a"}
              </Typography>
            )}
          </CardBase>
        );
      case CARD_IDS.estimatedHours:
        return (
          <CardBase
            key={cardId}
            title={estimatedAmountOfHoursLabel}
            useGlassCard={true}
            nested={true}
            sx={cardStyle}
          >
            <Typography variant="body1" fontWeight={500}>
              {data?.approximate_hours_to_complete || fixedRateLabel}
            </Typography>
          </CardBase>
        );
      case CARD_IDS.tasks:
        return (
          <CardBase
            key={cardId}
            title={tasksLabel}
            useGlassCard={true}
            nested={true}
            sx={cardStyle}
          >
            {data?.tasks?.length == 0 ? (
              <Typography variant="caption" color="text.secondary">
                {noTasksSpecifiedLabel}
              </Typography>
            ) : (
              <Box>
                {data?.tasks?.map((it, idx) => (
                  <Accordion
                    key={idx}
                    sx={{
                      boxShadow: "none",
                      border: (theme) =>
                        `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
                      borderRadius: "8px",
                      mb: 1.25,
                      "&:before": {
                        display: "none",
                      },
                      "&.Mui-expanded": {
                        margin: "0 0 10px 0",
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.05)"
                              : "rgba(0, 0, 0, 0.03)",
                        },
                      }}
                    >
                      <AssignmentOutlined sx={{ mr: 2, color: "primary.main" }} />
                      <Typography fontWeight={500}>{it.name}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                        {decode(it.description) || "n/a"}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </CardBase>
        );
      case CARD_IDS.requirements:
        return (
          <CardBase
            key={cardId}
            title={requirementsLabel}
            useGlassCard={true}
            nested={true}
            sx={cardStyle}
          >
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
                {skillsLabel}
              </Typography>
              {data?.skills?.length == 0 ? (
                <Typography
                  variant="caption"
                  sx={{ fontSize: 13, color: "text.secondary" }}
                >
                  {notRequiredLabel}
                </Typography>
              ) : (
                <Box sx={{ mb: 3 }}>
                  {data?.skills?.map((it) => (
                    <Chip
                      label={it.name}
                      key={it.id}
                      sx={{
                        mr: 1,
                        mb: 1,
                        borderRadius: "8px",
                        fontWeight: 500,
                      }}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
                {certificationsLabel}
              </Typography>
              {data?.certifications?.length == 0 ? (
                <Typography
                  variant="caption"
                  sx={{ fontSize: 13, color: "text.secondary" }}
                >
                  {notRequiredLabel}
                </Typography>
              ) : (
                <Box>
                  {data?.certifications?.map((it) => (
                    <Chip
                      label={it.name}
                      key={it.id}
                      sx={{
                        mr: 1,
                        mb: 1,
                        borderRadius: "8px",
                        fontWeight: 500,
                      }}
                      color="secondary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
            </Box>
          </CardBase>
        );
      case CARD_IDS.additionalSettings:
        return (
          <CardBase
            key={cardId}
            title={additionalSettingsLabel}
            useGlassCard={true}
            nested={true}
            sx={cardStyle}
          >
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="body2" fontWeight={500} display="inline">
                  {photosRequiredLabel}:{" "}
                </Typography>
                <Typography variant="body2" display="inline">
                  {!!data?.is_photo_required ? yesLabel : noLabel}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={500} display="inline">
                  {finalNotesRequiredLabel}:{" "}
                </Typography>
                <Typography variant="body2" display="inline">
                  {!!data?.is_final_notes_required ? yesLabel : noLabel}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={500} display="inline">
                  {toolsRequiredLabel}:{" "}
                </Typography>
                <Typography variant="body2" display="inline">
                  {!!data?.tool_required ? yesLabel : noLabel}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={500} display="inline">
                  {mileRadiusLabel}:{" "}
                </Typography>
                <Typography variant="body2" display="inline">
                  {data?.mile_radius} miles
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={500} display="inline">
                  {backgroundCheckRequiredLabel}:{" "}
                </Typography>
                <Typography variant="body2" display="inline">
                  {!!data?.background_check_required ? yesLabel : noLabel}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={500} display="inline">
                  {drugTestRequiredLabel}:{" "}
                </Typography>
                <Typography variant="body2" display="inline">
                  {!!data?.drug_test_required ? yesLabel : noLabel}
                </Typography>
              </Box>
            </Stack>
          </CardBase>
        );
      default:
        return null;
    }
  };

  // Helper function to render sidebar card by ID
  const renderSidebarCard = (cardId: string) => {
    const cardStyle = {
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: (theme: any) =>
          theme.palette.mode === "dark"
            ? "0 8px 24px rgba(0, 0, 0, 0.4)"
            : "0 8px 24px rgba(0, 0, 0, 0.1)",
      },
    };

    switch (cardId) {
      case CARD_IDS.workSite:
        return (
          <CardBase
            key={cardId}
            title={workSiteLabel}
            useGlassCard={true}
            nested={true}
            sx={cardStyle}
          >
            <Box
              sx={{
                borderRadius: "12px",
                overflow: "hidden",
                mb: 2,
                border: (theme) =>
                  `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
              }}
            >
              <MapPreview lat={lat} lng={lng} />
            </Box>
            {!!data?.location && (
              <Box>
                <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                  {data?.location?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {data?.location.address_1}, {data?.location.city},{" "}
                  {data?.location.state} {data?.location.zip}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <ContactPhone fontSize="small" color="disabled" />
                  <Typography variant="body2">
                    {data?.location?.primary_contact_name} -{" "}
                    {data?.location?.primary_contact_phone_number}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <EmailOutlined fontSize="small" color="disabled" />
                  <Typography variant="body2">
                    {data?.location?.primary_contact_email}
                  </Typography>
                </Stack>
              </Box>
            )}
            {!!data?.customer && (
              <Box>
                <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                  {data?.customer?.first_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {data?.customer.physical_address_1}, {data?.customer.city},{" "}
                  {data?.customer.state} {data?.customer.zip}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <ContactPhone fontSize="small" color="disabled" />
                  <Typography variant="body2">
                    {data?.customer?.phone_number}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <EmailOutlined fontSize="small" color="disabled" />
                  <Typography variant="body2">{data?.customer?.email}</Typography>
                </Stack>
              </Box>
            )}
          </CardBase>
        );
      case CARD_IDS.pricing:
        return (
          <CardBase
            key={cardId}
            title={pricingLabel}
            useGlassCard={true}
            nested={true}
            sx={cardStyle}
          >
            <Typography variant="h6" fontWeight={700} color="primary.main">
              {data?.type == "fixed" ? fixedRateLabel : hourlyRateLabel}: $
              {data?.type === "hourly"
                ? `${data.hourly_rate}/hr`
                : data?.total_pay}
            </Typography>
          </CardBase>
        );
      case CARD_IDS.manager:
        return (
          <CardBase
            key={cardId}
            title={managerLabel}
            useGlassCard={true}
            nested={true}
            sx={cardStyle}
          >
            <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>
              {data?.manager?.first_name} {data?.manager?.last_name}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <ContactPhone fontSize="small" color="disabled" />
              <Typography variant="body2">{data?.manager?.phone_number}</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <EmailOutlined fontSize="small" color="disabled" />
              <Typography variant="body2">{data?.manager?.email}</Typography>
            </Stack>
          </CardBase>
        );
      case CARD_IDS.internalNotes:
        return (
          <CardBase
            key={cardId}
            title={internalNotesLabel}
            useGlassCard={true}
            nested={true}
            headerActions={
              <IconButton
                size="small"
                onClick={() =>
                  setFormInternalNotesModal({ open: true, data: data! })
                }
                sx={{
                  "&:hover": {
                    backgroundColor: "primary.light",
                    color: "primary.main",
                  },
                }}
              >
                <EditOutlined />
              </IconButton>
            }
            sx={{
              ...cardStyle,
              "& > div > div:first-of-type": {
                [theme.breakpoints.down("sm")]: {
                  flexDirection: "row !important",
                  alignItems: "center !important",
                },
              },
            }}
          >
            <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
              {nl2br(data?.internal_notes || "n/a")}
            </Typography>
          </CardBase>
        );
      case CARD_IDS.metadata:
        return (
          <CardBase
            key={cardId}
            title={metadataLabel}
            useGlassCard={true}
            nested={true}
            sx={cardStyle}
          >
            <Stack spacing={1}>
              <Box>
                <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                  {createdAtLabel}
                </Typography>
                <Typography variant="body2">
                  {dayjs(data?.created_at).format("MMM DD, YYYY hh:mm A z")}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                  {lastUpdateLabel}
                </Typography>
                <Typography variant="body2">
                  {dayjs(data?.updated_at).format("MMM DD, YYYY hh:mm A z")}
                </Typography>
              </Box>
            </Stack>
          </CardBase>
        );
      default:
        return null;
    }
  };

  // Separate cards into left column and sidebar
  const leftColumnCards = [
    CARD_IDS.scopeOfWork,
    CARD_IDS.confidentialInfo,
    CARD_IDS.workDocuments,
    CARD_IDS.startTime,
    CARD_IDS.estimatedHours,
    CARD_IDS.tasks,
    CARD_IDS.requirements,
    CARD_IDS.additionalSettings,
  ];
  const sidebarCards = [
    CARD_IDS.workSite,
    CARD_IDS.pricing,
    CARD_IDS.manager,
    CARD_IDS.internalNotes,
    CARD_IDS.metadata,
  ];

  // Filter and order cards based on preferences
  const visibleLeftCards = cardOrder.filter(
    (id) => leftColumnCards.includes(id as any) && !hiddenCards.includes(id)
  );
  const visibleSidebarCards = cardOrder.filter(
    (id) => sidebarCards.includes(id as any) && !hiddenCards.includes(id)
  );

  return (
    <Box
      sx={{
        px: { xs: 0, sm: 1.5, md: 0 },
        py: { xs: 1.5, md: 2 },
      }}
    >
      <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          startIcon={<Settings />}
          onClick={() => setCustomizeLayoutModalOpen(true)}
          sx={{
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {customizeLayoutButtonLabel}
        </Button>
      </Box>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid item xs={12} md={8}>
          <Stack spacing={isMobile ? 1.75 : 2}>
            {visibleLeftCards.map((cardId) => renderCard(cardId as any))}
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack spacing={isMobile ? 1.75 : 2}>
            {visibleSidebarCards.map((cardId) => renderSidebarCard(cardId as any))}
            <Box>
              <Button
                variant="contained"
                onClick={() => navigate(`/works/${data?.id}/template/create`)}
                startIcon={<ContentCopy />}
                fullWidth
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.5,
                  boxShadow: 2,
                  "&:hover": {
                    boxShadow: 4,
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                {createTemplateFromThisWorkLabel}
              </Button>
            </Box>
          </Stack>
        </Grid>
      </Grid>
      <ModalStartDateForm
        open={formStartDateModal.open}
        onClose={() => setFormStartDateModal({ open: false, data: null })}
        data={formStartDateModal.data}
      />
      <ModalInternalNotesForm
        open={formInternalNotesModal.open}
        onClose={() => setFormInternalNotesModal({ open: false, data: null })}
        project={formInternalNotesModal.data}
      />
      <CustomizeLayoutModal
        open={customizeLayoutModalOpen}
        onClose={() => setCustomizeLayoutModalOpen(false)}
        preferences={cardPreferences}
        onSave={handleSaveCardPreferences}
      />
    </Box>
  );
};
export default ProjectDetailsPanel;
